import { type QueryHistoryItem } from "../hooks/useQueryHistory";

export interface QueryHistoryListProps {
  history: QueryHistoryItem[];
  onReQuery: (item: QueryHistoryItem) => void;
  onDelete: (id: string) => void;
  onClear: () => void;
}

function formatRelativeTime(isoString: string): string {
  const now = Date.now();
  const then = new Date(isoString).getTime();
  if (isNaN(then)) return isoString;

  const diffMs = now - then;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return "刚刚";
  if (diffMin < 60) return `${diffMin}分钟前`;
  if (diffHour < 24) return `${diffHour}小时前`;
  if (diffDay === 1) return "昨天";
  if (diffDay < 7) return `${diffDay}天前`;

  const d = new Date(isoString);
  return d.toLocaleDateString("zh-CN", { month: "short", day: "numeric" });
}

function StatusBadge({ status }: { status: QueryHistoryItem["status"] }) {
  const map: Record<QueryHistoryItem["status"], { cls: string; label: string; icon: string }> = {
    DONE:    { cls: "badge badge-success",   label: "成功", icon: "✓" },
    ERROR:   { cls: "badge badge-danger",    label: "失败", icon: "✗" },
    RUNNING: { cls: "badge badge-info",      label: "进行中", icon: "…" },
  };
  const s = map[status];
  return (
    <span className={s.cls}>
      <span>{s.icon}</span> {s.label}
    </span>
  );
}

function HistoryItem({
  item,
  onReQuery,
  onDelete,
}: {
  item: QueryHistoryItem;
  onReQuery: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="group flex items-center gap-4 px-4 py-3 rounded border-b border-border last:border-0 hover:bg-muted/40 transition-colors duration-150">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-medium text-foreground truncate">{item.drug_name}</span>
          {item.dosage_form && (
            <span className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">{item.dosage_form}</span>
          )}
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <StatusBadge status={item.status} />
          <span>{formatRelativeTime(item.created_at)}</span>
          {item.summary && (
            <span className="truncate max-w-[200px] opacity-70" title={item.summary}>
              {item.summary}
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-150 flex-shrink-0">
        <button
          onClick={onReQuery}
          className="btn btn-secondary h-7 px-2.5 text-xs"
          title="重新查询"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          查询
        </button>
        <button
          onClick={onDelete}
          className="btn btn-ghost h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
          title="删除此条记录"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      </div>
    </div>
  );
}

export default function QueryHistoryList({ history, onReQuery, onDelete, onClear }: QueryHistoryListProps) {
  const handleDelete = (id: string) => {
    if (window.confirm("确定要删除这条查询记录吗？")) {
      onDelete(id);
    }
  };

  const handleClearAll = () => {
    if (window.confirm("确定要清空所有查询历史吗？此操作不可恢复。")) {
      onClear();
    }
  };

  return (
    <div className="max-w-3xl mx-auto animate-fadeIn space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-foreground">📋 查询历史</h2>
          <p className="text-sm text-muted-foreground mt-1">共 {history.length} 条记录 · 最近 7 天的查询记录</p>
        </div>
        {history.length > 0 && (
          <button
            onClick={handleClearAll}
            className="btn btn-danger text-xs h-8"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            清空全部
          </button>
        )}
      </div>

      {history.length === 0 ? (
        <div className="card p-12 text-center animate-slideUp">
          <div className="flex flex-col items-center gap-3">
            <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center">
              <svg className="w-8 h-8 text-muted-foreground/50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-muted-foreground font-medium">暂无查询历史</p>
            <p className="text-sm text-muted-foreground/70">执行药品查询后，记录将自动保存在此处</p>
          </div>
        </div>
      ) : (
        <div className="card overflow-hidden animate-slideUp divide-y divide-border">
          {history.map((item) => (
            <HistoryItem
              key={item.id}
              item={item}
              onReQuery={() => onReQuery(item)}
              onDelete={() => handleDelete(item.id)}
            />
          ))}
        </div>
      )}

      <p className="text-center text-xs text-muted-foreground mt-4">
        历史记录仅存储在本地浏览器中，最多保留 200 条，7 天后自动清理
      </p>
    </div>
  );
}
